import {
  renderPhaseTypeInvariantShellHelpers,
  typeLabelForPhaseLabel,
} from "./bundle-ownership";
import {
  DEFAULT_RESOLVED_ISSUE_DEFAULTS,
  IssueDefaultsPriority,
  IssueDefaultsStatus,
  ResolvedIssueDefaults,
  labelsForPhase,
} from "./issue-defaults";
import {
  DEFAULT_GITHUB_ISSUE_TYPE,
  GithubIssueType,
  SET_ISSUE_TYPE_HELPER_PATH,
  githubIssueTypeForTitle,
} from "./issue-type";
import { AgentRuleBundle, IssueTemplatesConfig } from "../agent-config-options";

/**
 * Default master switch for the issue-templates convention. When no
 * config is supplied the convention ships **enabled** so every
 * configulator-consuming repo carries the canonical `gh issue create`
 * template reference in its rendered `CLAUDE.md`.
 *
 * @see IssueTemplatesConfig
 */
export const DEFAULT_ISSUE_TEMPLATES_ENABLED = true;

/**
 * Default repo-relative path for the consolidated issue-templates
 * documentation page. Matches the singleton `/docs` site layout every
 * configulator-managed repo ships: a single Starlight docs site at
 * `/docs` with agent reference pages under
 * `docs/src/content/docs/agents/`.
 *
 * The file is never generated by configulator unless `emitStarterDoc`
 * is set — the canonical list of templates is repo-specific and grows
 * whenever a new phase label is minted, so consumers author and evolve
 * the page themselves. The starter doc is opt-in.
 *
 * @see IssueTemplatesConfig
 */
export const DEFAULT_ISSUE_TEMPLATES_PATH =
  "docs/src/content/docs/agents/issue-templates.md";

/**
 * Default list of glob patterns that identify "bundle files" — the
 * source files that compose agent prompts and skill instructions.
 * These are the locations the optional lint walks when checking that
 * `gh issue create` snippets are **referenced** rather than inlined.
 *
 * The defaults cover the locations bundle-like content lives in a
 * generic configulator-consuming repo:
 *
 * - `.claude/agents/**.md` / `.claude/skills/**` — agent and skill
 *   prompts in consuming repos that don't re-export configulator
 *   bundles.
 *
 * Repos that **also** host configulator's own bundle source as a
 * workspace package (only `codedrifters/packages` itself) should
 * append `packages/@codedrifters/configulator/src/agent/bundles/**.ts`
 * via `IssueTemplatesConfig.bundlePathPatterns` to lint those bundle
 * sources too. The default omits that pattern because it is dead
 * weight (matches nothing) in any other consumer.
 *
 * Consumers can replace the list outright via `bundlePathPatterns`
 * when their agent sources live elsewhere.
 *
 * @see IssueTemplatesConfig
 */
export const DEFAULT_ISSUE_TEMPLATES_BUNDLE_PATH_PATTERNS: ReadonlyArray<string> =
  [".claude/agents/**/*.md", ".claude/skills/**/*.md"];

/**
 * Default for whether the convention emits the
 * `.claude/procedures/check-issue-templates.sh` lint to disk. The
 * script greps the provided files (stdin or positional args) for
 * inline `gh issue create` invocations and fails non-zero when any
 * are found outside a fenced example block that cites the canonical
 * templates doc.
 *
 * Disabled by default because many consumers prefer to enforce the
 * rule via review discipline and the rendered guidance alone; the
 * script is opt-in for repos that want a hard CI gate or pre-commit
 * hook.
 *
 * @see IssueTemplatesConfig
 */
export const DEFAULT_ISSUE_TEMPLATES_EMIT_CHECKER = false;

/**
 * Default for whether the convention emits the issue-templates
 * scaffold to disk. The scaffold is two files:
 *
 * 1. A **write-once** starter page at `<templatesPath>` (projen
 *    `SampleFile`) carrying the expected structure — the "How to use"
 *    preamble and one example `## Template: <phase-label>` section —
 *    which the consumer then fleshes out by hand.
 * 2. An **always-regenerated** companion page at
 *    {@link issueTemplatesGeneratedPath}, carrying one label-correct
 *    recipe stub per phase label the consumer's active bundles emit.
 *
 * The split exists because a `SampleFile` never reaches a consumer
 * whose page already exists: repos that adopted the convention on an
 * older configulator would otherwise be frozen on whatever skeleton
 * shipped that day. Hand-authored bodies stay in the write-once page;
 * the generated label sets regenerate on every `projen` run so a new
 * phase label reaches every consumer on their next upgrade.
 *
 * Disabled by default because the hand-authored page conflicts with
 * the ad-hoc notes most repos already maintain when they adopt the
 * convention.
 *
 * @see IssueTemplatesConfig
 */
export const DEFAULT_ISSUE_TEMPLATES_EMIT_STARTER = false;

/**
 * Filename suffix appended to the templates page's stem to derive the
 * always-regenerated companion page. Chosen so the companion can never
 * collide with a hand-authored router layout that splits recipes into
 * `<stem>/<child>.md` sibling pages.
 */
export const ISSUE_TEMPLATES_GENERATED_SUFFIX = "-generated";

/**
 * Repo-relative path of the always-regenerated companion page for a
 * given `templatesPath`: the stem gains
 * {@link ISSUE_TEMPLATES_GENERATED_SUFFIX} and keeps its extension
 * (`…/issue-templates.md` → `…/issue-templates-generated.md`).
 */
export function issueTemplatesGeneratedPath(templatesPath: string): string {
  const slash = templatesPath.lastIndexOf("/");
  const dot = templatesPath.lastIndexOf(".");
  if (dot > slash) {
    return `${templatesPath.slice(0, dot)}${ISSUE_TEMPLATES_GENERATED_SUFFIX}${templatesPath.slice(dot)}`;
  }
  return `${templatesPath}${ISSUE_TEMPLATES_GENERATED_SUFFIX}`;
}

/**
 * Glob matching the sibling child pages of a router-style templates
 * layout (`…/issue-templates.md` → `…/issue-templates/*.md`). The
 * label-consistency lint walks these so a repo that split its recipes
 * across child pages is checked against the same map.
 */
export function issueTemplatesChildGlob(templatesPath: string): string {
  const slash = templatesPath.lastIndexOf("/");
  const dot = templatesPath.lastIndexOf(".");
  const stem = dot > slash ? templatesPath.slice(0, dot) : templatesPath;
  return `${stem}/*.md`;
}

/**
 * Default for whether the rendered rule body asserts that every
 * `gh issue create` recipe in a bundle or agent prompt **MUST** cite
 * the canonical templates doc rather than inline a full template.
 *
 * Defaults to `true` — the whole point of consolidation is that
 * templates live in one place, so the MUST phrasing is the correct
 * default. Consumers that treat consolidation as aspirational can
 * soften the phrasing by setting this to `false`.
 *
 * @see IssueTemplatesConfig
 */
export const DEFAULT_ISSUE_TEMPLATES_REQUIRE_REFERENCE = true;

/**
 * Fully-resolved issue-templates settings. Every field is defaulted
 * so downstream renderers can reason about a single canonical shape.
 */
export interface ResolvedIssueTemplates {
  readonly enabled: boolean;
  readonly templatesPath: string;
  readonly bundlePathPatterns: ReadonlyArray<string>;
  readonly emitChecker: boolean;
  readonly emitStarterDoc: boolean;
  readonly requireReference: boolean;
}

/**
 * Resolve a (possibly absent) `IssueTemplatesConfig` into a canonical
 * `ResolvedIssueTemplates` with every field filled in. Unset fields
 * cascade from their documented defaults.
 *
 * Malformed configs — empty / whitespace-only or absolute
 * `templatesPath`, empty `bundlePathPatterns`, empty /
 * whitespace-only path entry — throw a descriptive `Error`.
 */
export function resolveIssueTemplates(
  config?: IssueTemplatesConfig,
): ResolvedIssueTemplates {
  const templatesPath = config?.templatesPath ?? DEFAULT_ISSUE_TEMPLATES_PATH;
  assertValidTemplatesPath(templatesPath);

  const bundlePathPatterns =
    config?.bundlePathPatterns ?? DEFAULT_ISSUE_TEMPLATES_BUNDLE_PATH_PATTERNS;
  assertValidBundlePathPatterns(bundlePathPatterns);

  return {
    enabled: config?.enabled ?? DEFAULT_ISSUE_TEMPLATES_ENABLED,
    templatesPath,
    bundlePathPatterns,
    emitChecker: config?.emitChecker ?? DEFAULT_ISSUE_TEMPLATES_EMIT_CHECKER,
    emitStarterDoc:
      config?.emitStarterDoc ?? DEFAULT_ISSUE_TEMPLATES_EMIT_STARTER,
    requireReference:
      config?.requireReference ?? DEFAULT_ISSUE_TEMPLATES_REQUIRE_REFERENCE,
  };
}

/**
 * Synth-time validation hook. Throws a descriptive `Error` when the
 * supplied `IssueTemplatesConfig` is malformed. Called by
 * `AgentConfig.preSynthesize` before any rendering so a misconfigured
 * convention fails the build instead of silently shipping broken
 * guidance. Returns the resolved config unchanged so callers can
 * write `const it = validateIssueTemplatesConfig(config)` in one line.
 *
 * Malformed cases rejected here:
 *
 * - `templatesPath` empty, whitespace-only, or absolute.
 * - `bundlePathPatterns` not an array, empty, or contains an empty /
 *   whitespace-only entry.
 */
export function validateIssueTemplatesConfig(
  config?: IssueTemplatesConfig,
): ResolvedIssueTemplates {
  return resolveIssueTemplates(config);
}

/**
 * Render the full body for the `issue-templates-convention` rule
 * shipped by the `base` bundle. The rule documents:
 *
 * - Why the convention exists (drift between duplicated
 *   `gh issue create` snippets across bundles).
 * - The on-disk contract — a single hand-authored page at
 *   `<templatesPath>` with one `## Template: <phase-label>` section
 *   per downstream issue kind.
 * - The **reference-don't-inline** rule, phrased as a hard
 *   requirement or a strong recommendation per `requireReference`.
 * - The set of paths the rule applies to.
 * - The optional lint script (cross-referenced only when emitted).
 *
 * When the convention is disabled, the rule renders a short stub.
 */
export function renderIssueTemplatesRuleContent(
  it: ResolvedIssueTemplates,
  hasDownstreamBundles: boolean = true,
): string {
  if (!it.enabled || !hasDownstreamBundles) {
    return [
      "# Issue Templates",
      "",
      "**The issue-templates convention is disabled in this project.**",
      "Bundles and agent prompts may inline full `gh issue create`",
      "recipes locally. When two bundles drift apart on title format,",
      "label set, or body shape, humans must reconcile them manually.",
      "",
      "Enable the convention via",
      "`AgentConfigOptions.issueTemplates.enabled = true`.",
    ].join("\n");
  }

  const requirementVerb = it.requireReference ? "MUST" : "SHOULD";
  const requirementAdverb = it.requireReference ? "must" : "should";

  const lines: Array<string> = [
    "# Issue Templates",
    "",
    "This project maintains a single hand-authored issue-templates",
    "reference page that lists one canonical `gh issue create` recipe",
    "per downstream issue kind (`company:research`, `research:scope`,",
    "`req:write`, `meeting:notes`, …). Bundle rules and agent prompts",
    "that file downstream issues cite the matching section of that",
    "page instead of inlining a fresh copy of the command.",
    "",
    "Without this convention, each phased-agent bundle grows its own",
    "copy of the `gh issue create` invocation it dispatches. The title",
    "format, the label set, and the body template drift subtly across",
    "bundles over time — a new required label lands in one bundle but",
    "not the other; one bundle still cites the old `status:new` label",
    "while another uses the canonical `status:ready`; the body",
    "template's `## Objective` section is called `## Purpose` in half",
    "the bundles. Consolidation removes the drift surface.",
    "",
    "The canonical label taxonomy (`type:*`, `priority:*`, `status:*`,",
    "phase label, `tier:*`) remains authoritative via the",
    "`priority-rule` / `issue-label-conventions` / tier modules — the",
    "issue-templates page is a **thin recipe catalogue** over that",
    "taxonomy, not a second source of truth for it.",
    "",
    "## Templates Page",
    "",
    `The issue-templates page lives at \`${it.templatesPath}\`. It`,
    "follows a stable structure so the lint rule (and humans reading",
    "the page) can mechanically identify which section matches which",
    "phase label:",
    "",
    "1. **How to use** — a short prose preamble that cites the label",
    "   conventions (`priority:*`, `status:*`) and the duplicate-check",
    "   protocol agents apply before filing a new issue.",
    "2. **One section per phase label** — a `## Template: <phase-label>`",
    "   heading followed by a single fenced `bash` block containing",
    "   the canonical `gh issue create` invocation for that kind of",
    "   issue. Bodies use angle-bracketed placeholders (`<Organization",
    "   Name>`, `<parent-issue>`, `<slug>`) that callers substitute at",
    "   invocation time.",
    "3. **Callers list** — each template section names the bundles /",
    "   agents that file that kind of issue, so a reader can trace",
    "   back from a template to every workflow that emits it.",
    "",
    "Each template section is independent. Adding a new downstream",
    "issue kind appends one section; retiring a kind removes one. The",
    "label taxonomy, body template shape, and placeholder conventions",
    "are shared across every section.",
    "",
  ];

  if (it.emitStarterDoc) {
    lines.push(
      "## Scaffolded Starting Point",
      "",
      "The page is hand-authored, but it does not start empty. Two files",
      "ship together:",
      "",
      `1. \`${it.templatesPath}\` — **written once**, then yours. Titles,`,
      "   bodies, and the callers list are hand-authored here and never",
      "   overwritten.",
      `2. \`${issueTemplatesGeneratedPath(it.templatesPath)}\` —`,
      "   **regenerated on every synth**. One",
      "   `## Template: <phase-label>` stub per phase label the active",
      "   bundles contribute to the label registry, with the phase label,",
      "   the `type:<bundle>` label its owning bundle declares, and the",
      "   configured `status:*` / `priority:*` defaults pre-filled.",
      "",
      "Only the **label set** is generated. Title and body stay",
      "angle-bracket placeholders, so the scaffold answers *which labels*",
      "without dictating *what the issue says*. Copy a stub into the",
      "hand-authored page, flesh out its body, and leave the label block",
      "as generated.",
      "",
      "The split is load-bearing. A write-once scaffold alone freezes",
      "every consumer on whatever skeleton shipped the day they adopted",
      "the convention — a new phase label minted upstream never reaches",
      "them. Regenerating the label half fixes that without ever",
      "clobbering an authored body.",
      "",
    );
  }

  lines.push(
    "## Reference-Don't-Inline Rule",
    "",
    `Every bundle rule, agent prompt, or skill instruction that files a`,
    `downstream issue ${requirementVerb} cite the matching section of`,
    `\`${it.templatesPath}\` instead of inlining a full`,
    "`gh issue create` invocation. The rule applies when any file in a",
    "commit or PR matches one of the bundle-path patterns below:",
    "",
    ...it.bundlePathPatterns.map((p) => `- \`${p}\``),
    "",
    "Mechanically: a bundle file may **mention** `gh issue create` in",
    "prose (to describe the step), cite the templates page as the",
    "source of record, or reproduce a single-line fragment for",
    `illustration. It ${requirementAdverb} **not** include a full`,
    "multi-label `gh issue create --title ... --label ... --body ...`",
    "recipe inline; that recipe belongs in the templates page.",
    "",
    "### What counts as an inline template",
    "",
    "The rule targets duplicate recipes, not any mention of the CLI.",
    "Concretely:",
    "",
    "- A multi-line `gh issue create` block with `--title`, `--label`,",
    "  and `--body` arguments — that is a full template and must live",
    "  in the templates page.",
    '- A line like `--label "type:foo" --label "status:ready"` in a',
    "  bundle rule without pointing at the templates page — that is",
    "  the label set half of a template and invites drift.",
    "- A `## Issue Body Template` section that duplicates the template",
    "  body in a bundle's workflow rule — the body belongs in the",
    "  templates page section.",
    "",
    "Edits that do **not** require moving to the templates page:",
    "",
    "- Prose that mentions `gh issue create` without supplying a full",
    '  recipe (e.g. "Phase 3 files a `research:scope` issue using the',
    '  template documented in the issue-templates page.").',
    "- A single-line illustrative fragment (e.g.",
    "  `` `gh issue create --title '<type>: <description>'` ``) used",
    "  to document a conventional prefix or title format — those are",
    "  taxonomy, not templates.",
    "- The canonical `create-issue-workflow` rule itself, which",
    "  documents how humans type `create an issue` to invoke the",
    "  workflow — that rule defines the taxonomy and is the one",
    "  allowed place a full `gh issue create` call lives outside the",
    "  templates page.",
    "",
    "## Authoring Checklist",
    "",
    "Authors and reviewers walk the following checklist before merging",
    "any PR that touches a bundle path and mentions `gh issue create`:",
    "",
    `1. **Is the recipe in the templates page?** If not, add a`,
    `   \`## Template: <phase-label>\` section to \`${it.templatesPath}\``,
    "   before (or in the same commit as) the bundle change that needs",
    "   it. The templates page is the first place a new recipe lands.",
    "2. **Does the bundle rule cite the page?** The rule / prompt that",
    "   dispatches the issue should name the specific",
    "   `## Template: <phase-label>` section instead of reproducing",
    "   the recipe.",
    "3. **Do the labels match the taxonomy?** Every template recipe",
    "   must carry exactly one `type:*` label, exactly one",
    "   `priority:*` label, exactly one `status:*` label, and the",
    "   phase label that identifies the downstream agent. Tier labels",
    "   (`tier:platform`, `tier:industry`, …) are additional when the",
    "   downstream agent is tier-aware.",
    "4. **Are placeholder names stable?** Use angle-bracket",
    "   placeholders (`<Organization Name>`, `<slug>`) that match",
    "   existing templates. Consistency across sections lets callers",
    "   substitute without re-reading the body every time.",
    "5. **Does the recipe assign a GitHub issue type?** `gh issue",
    "   create` cannot set it and the `type:*` label does not either, so",
    "   every section ends with the follow-up call. An issue that skips",
    "   it stays untyped forever.",
    "",
  );

  if (it.emitChecker) {
    lines.push(
      "## Automated Lint",
      "",
      "The bundled `.claude/procedures/check-issue-templates.sh`",
      "helper enforces the rule mechanically. Given a set of changed",
      "files (as a newline-separated list on stdin or positional",
      "arguments), it fails non-zero when any file matches a",
      "bundle-path pattern and contains a multi-line",
      "`gh issue create ... --title ... --label ...` invocation. It",
      "exits `0` when no bundle files were touched or when every",
      "`gh issue create` mention is a single-line fragment, a prose",
      "reference, or lives in an allow-listed path.",
      "",
      "```bash",
      "# Pre-commit (staged files):",
      "git diff --cached --name-only \\",
      "  | .claude/procedures/check-issue-templates.sh",
      "",
      "# CI (branch-vs-base):",
      "git diff --name-only origin/main...HEAD \\",
      "  | .claude/procedures/check-issue-templates.sh",
      "```",
      "",
      "The lint allow-lists two paths by default: the templates page",
      `itself (\`${it.templatesPath}\`) and the`,
      "`github-workflow` bundle's `create-issue-workflow` rule source",
      "(which documents the conventional `gh issue create` call shape",
      "as taxonomy, not a downstream template). All other bundle",
      "files in the pattern set are in scope.",
      "",
      "### Recipe label correctness",
      "",
      "A companion lint,",
      "`.claude/procedures/check-issue-template-labels.sh`, checks the",
      "recipes themselves rather than where they live. It walks the",
      "templates page, its router-style child pages, and the generated",
      "label-set companion, and fails non-zero when a",
      "`## Template: <phase-label>` section:",
      "",
      "- never passes `--label <phase-label>` — the heading and the",
      "  command disagree;",
      "- carries no `type:*` label, more than one, or one that is not",
      "  the `type:<bundle>` label the phase label's owning bundle",
      "  declares;",
      "- has no GitHub issue-type assignment step.",
      "",
      "```bash",
      ".claude/procedures/check-issue-template-labels.sh",
      "```",
      "",
      "The phase-label → type-label pairing is generated from the same",
      "bundle-ownership map that drives `.github/labels.yml` and the",
      "orchestrator's invariant sweep, so a hand-edit can never",
      "reintroduce a mismatch the registry has already ruled out.",
      "Sections whose heading matches no bundle-owned phase label are",
      "skipped — consumer-specific labels are deliberately not policed.",
      "",
    );
  }

  lines.push(
    "## Out of Scope",
    "",
    "The convention intentionally does not:",
    "",
    "- Auto-generate recipe **bodies**. Only the label set is",
    "  scaffolded, because labels are derivable from the bundle",
    "  registry and bodies are not — repo-specific objectives,",
    "  acceptance criteria, and callers lists stay hand-authored.",
    "- Prescribe a fixed body shape across templates. Each section",
    "  may carry its own `## Acceptance Criteria`, `## Scope Size`,",
    "  or `## Output Path` fields as the downstream agent requires;",
    "  the convention only asserts the recipe lives in one place.",
    "- Gate every unrelated cleanup PR on a templates-page touch.",
    "  The rule targets inline `gh issue create` recipes; pure",
    "  cleanups that add no new recipe are exempt.",
  );

  return lines.join("\n");
}

/**
 * Render the short issue-templates hook section injected into a
 * phased-agent bundle's workflow rule. The section cites the full
 * contract documented in the base bundle's
 * `issue-templates-convention` rule so individual bundles stay DRY.
 *
 * When the convention is disabled, the function returns an empty
 * string so callers can no-op their append path.
 */
export function renderIssueTemplatesBundleHook(
  it: ResolvedIssueTemplates,
  bundleLabel: string,
): string {
  if (!it.enabled) {
    return "";
  }

  const verb = it.requireReference ? "must" : "should";

  return [
    "## Issue Templates",
    "",
    `Every downstream \`gh issue create\` invocation dispatched by the`,
    `${bundleLabel} bundle ${verb} cite the matching`,
    `\`## Template: <phase-label>\` section in \`${it.templatesPath}\``,
    "instead of inlining a full template. Inline `--title ...`",
    "`--label ... --body ...` blocks drift across bundles; the",
    "consolidated page is the single source of record for the recipe,",
    "the placeholder names, and the label set. See the",
    "`issue-templates-convention` rule for the full authoring",
    "checklist, the path patterns the rule covers, and what counts",
    "as an inline template vs. a prose mention.",
  ].join("\n");
}

/**
 * Render the write-once starter issue-templates page — the frontmatter,
 * the "How to use" preamble, a pointer at the always-regenerated
 * label-set companion, and a single example template section. Exported
 * so `AgentConfig` can emit it to disk when the consumer opts in via
 * `emitStarterDoc: true`.
 *
 * The starter stays deliberately sparse on **bodies**: it documents the
 * expected structure without committing the consumer to a particular
 * body shape. The correct-by-construction **label sets** live in the
 * companion page this one links to, which regenerates on every synth —
 * so a write-once starter can never freeze a consumer on a stale label
 * taxonomy.
 */
export function renderIssueTemplatesStarterPage(
  it: ResolvedIssueTemplates,
): string {
  const generatedLink = `./${basename(issueTemplatesGeneratedPath(it.templatesPath))}`;
  return [
    "---",
    "title: Issue Templates",
    "description: Canonical gh issue create recipes — one per downstream phase label.",
    "---",
    "",
    "This page lists the canonical `gh issue create` recipe for every",
    "downstream issue kind dispatched by an agent in this monorepo.",
    "Bundles and agent prompts cite the matching section below instead",
    "of inlining the recipe — see the",
    "`issue-templates-convention` rule for the full contract.",
    "",
    "## How to use",
    "",
    "Each template shows the full `gh issue create` command with",
    "angle-bracketed placeholders (`<...>`). Replace placeholders with",
    "real values before running. Every recipe carries exactly one",
    "`type:*` label, exactly one `priority:*` label, and exactly one",
    "`status:*` label from the conventions documented in the",
    "`issue-label-conventions` rule.",
    "",
    "Every recipe also ends with a **GitHub issue type** assignment.",
    "`gh issue create` cannot set the issue type, so it takes a second",
    "call — an issue that skips it stays untyped forever. Derive the type",
    "from the title prefix (`epic:` → Epic, `feat:` → Feature, `fix:` →",
    "Bug, everything else — including every bundle-phase prefix — →",
    "Task); see **Assigning the GitHub issue type** in the",
    "`issue-conventions` rule for the command and its fallback.",
    "",
    "Before creating any profile-style issue (people, companies,",
    "software), run the duplicate check documented in the owning",
    "bundle's rule — the issue-templates page is a recipe catalogue,",
    "not a duplicate-check layer.",
    "",
    "## Generated label sets",
    "",
    `[Generated Issue-Template Label Sets](${generatedLink}) carries one`,
    "correct-by-construction stub per phase label the active bundles",
    "emit — the phase label paired with the `type:<bundle>` label its",
    "owning bundle declares, plus the configured `status:*` /",
    "`priority:*` defaults. That page regenerates on every `projen` run,",
    "so it never goes stale as phase labels come and go.",
    "",
    "This page is the opposite: written once, then owned by you. Copy a",
    "stub across, flesh out its title and body here, and leave the label",
    "block exactly as generated — the label-consistency lint holds both",
    "copies to the same pairing.",
    "",
    "## Template: example-phase",
    "",
    "Replace this example with the real templates for this repo. Each",
    "section is independent; add or remove sections as phase labels",
    "come and go.",
    "",
    "**Callers:** Name the bundles and agents that file this kind of",
    "issue.",
    "",
    "```bash",
    "gh issue create \\",
    '  --title "<type>: <short description>" \\',
    '  --label "type:<type>" \\',
    '  --label "<phase-label>" \\',
    '  --label "status:ready" \\',
    '  --label "<priority-label>" \\',
    '  --body "## Objective',
    "",
    "<1-3 sentences describing the issue>.",
    "",
    "## Context",
    "",
    "- **Discovered in:** <source description> (#<parent-issue>)",
    "",
    "## Acceptance Criteria",
    "",
    "- [ ] <criterion 1>",
    "- [ ] <criterion 2>",
    '"',
    "```",
    "",
    "Then assign the GitHub issue type (`Task` for a phase-labelled",
    "title):",
    "",
    "```bash",
    `${SET_ISSUE_TYPE_HELPER_PATH} <issue-number> ${DEFAULT_GITHUB_ISSUE_TYPE}`,
    "```",
  ].join("\n");
}

/*******************************************************************************
 *
 * Generated recipe stubs
 *
 ******************************************************************************/

/**
 * One correct-by-construction recipe stub: a phase label plus every
 * label the recipe must carry, all derived rather than hand-copied.
 */
export interface IssueTemplateRecipeStub {
  /** The phase label the recipe files (e.g. `people:research`). */
  readonly phaseLabel: string;
  /** The `type:<bundle>` label the phase-label invariant requires. */
  readonly typeLabel: string;
  /** Bundle that contributes the phase label to `.github/labels.yml`. */
  readonly bundleName: string;
  /** The label's registry description, used as the section blurb. */
  readonly description: string;
  /** Effective `status:*` value for this phase. */
  readonly status: IssueDefaultsStatus;
  /** Effective `priority:*` value for this phase. */
  readonly priority: IssueDefaultsPriority;
  /** GitHub issue type the filed issue must be assigned. */
  readonly issueType: GithubIssueType;
}

/**
 * Derive one recipe stub per phase label the supplied bundles
 * contribute to `.github/labels.yml`.
 *
 * A contributed label counts as a phase label exactly when
 * `typeLabelForPhaseLabel` resolves it — i.e. when the canonical
 * bundle-ownership map claims it. That is the *same* map that drives
 * the label registry and the orchestrator's phase-label invariant, so
 * a generated stub can never pair a phase label with the wrong
 * `type:<bundle>` label. Consumer-specific labels no bundle owns are
 * skipped rather than guessed at.
 *
 * Results are deduplicated by phase label (co-owned `req:*` labels are
 * contributed by more than one requirements bundle) and sorted so the
 * rendered page is stable across synth runs.
 */
export function collectIssueTemplateRecipeStubs(
  bundles: ReadonlyArray<AgentRuleBundle>,
  issueDefaults: ResolvedIssueDefaults = DEFAULT_RESOLVED_ISSUE_DEFAULTS,
): ReadonlyArray<IssueTemplateRecipeStub> {
  const stubs = new Map<string, IssueTemplateRecipeStub>();
  for (const bundle of bundles) {
    for (const label of bundle.labels ?? []) {
      const typeLabel = typeLabelForPhaseLabel(label.name);
      if (typeLabel === undefined) {
        continue;
      }
      if (stubs.has(label.name)) {
        continue;
      }
      const defaults = labelsForPhase(issueDefaults, label.name);
      stubs.set(label.name, {
        phaseLabel: label.name,
        typeLabel,
        bundleName: bundle.name,
        description: label.description ?? "",
        status: defaults.status,
        priority: defaults.priority,
        issueType: githubIssueTypeForTitle(`${label.name}: placeholder`),
      });
    }
  }
  return [...stubs.values()].sort((a, b) =>
    a.phaseLabel.localeCompare(b.phaseLabel),
  );
}

/**
 * Render the always-regenerated companion page that carries one
 * label-correct `## Template: <phase-label>` stub per phase label the
 * consumer's active bundles emit.
 *
 * Only the **label set** and the issue-type assignment are generated —
 * title and body stay angle-bracket placeholders, so the page is a
 * correct-by-construction starting point rather than a second source of
 * truth for recipe bodies. Consumers move a stub into their
 * hand-authored templates page and flesh out its body there; the
 * label-consistency lint then holds both copies to the same pairing.
 */
export function renderIssueTemplatesGeneratedPage(
  it: ResolvedIssueTemplates,
  stubs: ReadonlyArray<IssueTemplateRecipeStub>,
): string {
  const lines: Array<string> = [
    "---",
    "title: Generated Issue-Template Label Sets",
    "description: Auto-generated label-correct gh issue create stubs — one per phase label.",
    "---",
    "",
    "**Generated file — do not edit.** Every section below is",
    "regenerated from the active bundle set on each `projen` run; hand",
    "edits are overwritten. The hand-authored recipes live in",
    `[the issue-templates page](./${basename(it.templatesPath)}) — this`,
    "page exists so the **label half** of every recipe stays",
    "correct-by-construction as phase labels come and go.",
    "",
    "## How to use",
    "",
    "Each section carries the exact label set the phase-label invariant",
    "requires: the phase label itself, the `type:<bundle>` label its",
    "owning bundle declares, and the configured `status:*` / `priority:*`",
    "defaults for that phase. Title and body are placeholders — copy the",
    "stub into the hand-authored templates page and flesh out the body",
    "there, or run it as-is when no repo-specific body exists yet.",
    "",
    "Every stub ends with the **GitHub issue type** assignment. `gh issue",
    "create` cannot set the issue type and the `type:*` label does not",
    "set it either, so an issue that skips the follow-up call stays",
    "untyped forever. See **Assigning the GitHub issue type** in the",
    "`issue-conventions` rule for the no-helper fallback.",
    "",
  ];

  if (stubs.length === 0) {
    lines.push(
      "## No phase labels",
      "",
      "No active bundle contributes a phase label, so there is nothing to",
      "scaffold. Enable a phased-pipeline bundle (or drop it from",
      "`excludeBundles`) and re-run `projen`.",
    );
    return lines.join("\n");
  }

  for (const stub of stubs) {
    lines.push(
      `## Template: ${stub.phaseLabel}`,
      "",
      ...(stub.description === "" ? [] : [stub.description, ""]),
      `**Owning bundle:** \`${stub.bundleName}\` — **required type label:**`,
      `\`${stub.typeLabel}\` (paired by the bundle-ownership map).`,
      "",
      "**Callers:** <name the bundles and agents that file this issue>",
      "",
      "```bash",
      "gh issue create \\",
      `  --title "${stub.phaseLabel}: <short description>" \\`,
      `  --label "${stub.typeLabel}" \\`,
      `  --label "${stub.phaseLabel}" \\`,
      `  --label "priority:${stub.priority}" \\`,
      `  --label "status:${stub.status}" \\`,
      '  --body "## Objective',
      "",
      "<1-3 sentences describing the work>.",
      "",
      "## Context",
      "",
      "- **Discovered in:** <source description> (#<parent-issue>)",
      "",
      "## Acceptance Criteria",
      "",
      "- [ ] <criterion 1>",
      '"',
      "```",
      "",
      `Then assign the GitHub issue type (\`${stub.issueType}\`):`,
      "",
      "```bash",
      `${SET_ISSUE_TYPE_HELPER_PATH} <issue-number> ${stub.issueType}`,
      "```",
      "",
    );
  }

  // Drop the trailing blank so the file ends on a single newline.
  lines.pop();
  return lines.join("\n");
}

/**
 * Render the `.claude/procedures/check-issue-templates.sh` helper
 * script. Exported so `AgentConfig` can register it as an
 * `AgentProcedure` when the consumer opts in via `emitChecker: true`.
 *
 * The script accepts the list of changed files as either:
 *
 * 1. Positional arguments (one file per arg).
 * 2. Newline-separated entries on stdin (when no args supplied) —
 *    pipe `git diff --name-only` directly into it.
 *
 * It fails non-zero when any changed file matches a bundle-path
 * pattern and contains a multi-line `gh issue create ... --title`
 * invocation that isn't in the configured allow list (the templates
 * page itself and the `create-issue-workflow` rule source).
 */
export function renderIssueTemplatesCheckerScript(
  it: ResolvedIssueTemplates,
): string {
  const patternsLiteral = it.bundlePathPatterns
    .map((p) => `  ${JSON.stringify(p)}`)
    .join("\n");

  return [
    "#!/usr/bin/env bash",
    "# check-issue-templates.sh — Enforce the reference-don't-inline rule.",
    "#",
    "# Usage:",
    "#   .claude/procedures/check-issue-templates.sh <file>...",
    "#   git diff --name-only HEAD | .claude/procedures/check-issue-templates.sh",
    "#",
    "# Fails non-zero when any changed file matches a bundle-path pattern",
    "# and contains a multi-line `gh issue create` invocation — i.e. one",
    "# that includes a `--title` flag on a subsequent line. Single-line",
    "# fragments and prose mentions are permitted.",
    "#",
    "# Five paths are allow-listed: the templates page itself, the",
    "# `github-workflow` bundle's `create-issue-workflow` rule source,",
    "# the `upstream-configulator-docs` bundle's rule source, the",
    "# `issue-templates` bundle source (which renders the templates",
    "# page and so necessarily embeds the recipes as its content), and",
    "# the `issue-templates` test source (which contains recipe fixtures",
    "# used to exercise this very lint). All five legitimately contain",
    "# `gh issue create` recipes as taxonomy, content, or test fixtures.",
    "#",
    "# The bundle-path patterns and templates path are rendered from the",
    "# consumer's IssueTemplatesConfig at synth time — do not edit by",
    "# hand; regenerate via `pnpm exec projen`.",
    "#",
    "# Patterns support the `**` recursive-segment wildcard even though",
    "# bash's native `[[ $file == $glob ]]` pattern matching does not:",
    "# each glob is converted to a POSIX-extended regex internally and",
    "# matched with `=~`, so the script works uniformly on bash 3.2",
    "# (macOS default) and bash 4+ (Linux / CI) without depending on",
    "# `shopt -s globstar`.",
    "",
    "set -uo pipefail",
    "",
    "err() {",
    '  printf "check-issue-templates.sh: %s\\n" "$*" >&2',
    "}",
    "",
    `templates_path=${JSON.stringify(it.templatesPath)}`,
    "",
    "# Allow-listed paths: the templates page itself, the",
    "# create-issue-workflow rule source, the upstream-configulator-docs",
    "# rule source, and the issue-templates bundle source + test (which",
    "# render and exercise the lint and so necessarily embed recipes).",
    "allowlist=(",
    `  "$templates_path"`,
    '  "packages/@codedrifters/configulator/src/agent/bundles/github-workflow.ts"',
    '  "packages/@codedrifters/configulator/src/agent/bundles/upstream-configulator-docs.ts"',
    '  "packages/@codedrifters/configulator/src/agent/bundles/issue-templates.ts"',
    '  "packages/@codedrifters/configulator/src/agent/bundles/issue-templates.test.ts"',
    ")",
    "",
    "# Bundle-path glob patterns. Translated to anchored POSIX-extended",
    "# regexes at runtime by `glob_to_regex` below so `**` matches",
    "# recursively across path segments.",
    "bundle_patterns=(",
    patternsLiteral,
    ")",
    "",
    "# Convert a bash-style glob into an anchored POSIX-extended regex:",
    "#   **/   → (.+/)?   (zero or more path segments plus trailing slash)",
    "#   **    → .*       (anywhere, including `/`)",
    "#   *     → [^/]*    (single path segment)",
    "#   ?     → [^/]     (single non-slash char)",
    "# Every other char is passed through, with regex metacharacters",
    "# backslash-escaped so literal `.` / `+` / `(` etc. match themselves.",
    "glob_to_regex() {",
    '  local glob="$1"',
    '  local regex=""',
    "  local i=0",
    "  local len=${#glob}",
    "  while (( i < len )); do",
    '    local c="${glob:i:1}"',
    '    if [[ "$c" == "*" ]]; then',
    '      local next="${glob:i+1:1}"',
    '      if [[ "$next" == "*" ]]; then',
    '        local after="${glob:i+2:1}"',
    '        if [[ "$after" == "/" ]]; then',
    '          regex+="(.+/)?"',
    "          i=$(( i + 3 ))",
    "        else",
    '          regex+=".*"',
    "          i=$(( i + 2 ))",
    "        fi",
    "      else",
    '        regex+="[^/]*"',
    "        i=$(( i + 1 ))",
    "      fi",
    '    elif [[ "$c" == "?" ]]; then',
    '      regex+="[^/]"',
    "      i=$(( i + 1 ))",
    "    else",
    '      case "$c" in',
    "        '.'|'+'|'^'|'$'|'('|')'|'{'|'}'|'|'|'['|']'|'\\\\')",
    '          regex+="\\\\$c"',
    "          ;;",
    "        *)",
    '          regex+="$c"',
    "          ;;",
    "      esac",
    "      i=$(( i + 1 ))",
    "    fi",
    "  done",
    '  printf "^%s$" "$regex"',
    "}",
    "",
    "# Check whether a file has a multi-line `gh issue create` recipe.",
    "# Heuristic: the file contains a line matching `gh issue create`",
    "# followed — within the next 20 lines — by a line carrying a",
    "# `--title` flag. Single-line fragments and prose mentions that",
    "# don't include `--title` on a near-by line are permitted.",
    "has_multiline_recipe() {",
    '  local file="$1"',
    "  awk '",
    "    /gh issue create/ { hit = NR }",
    '    hit && NR > hit && NR - hit <= 20 && /--title/ { print "FOUND"; exit }',
    '  \' "$file" 2>/dev/null | grep -q FOUND',
    "}",
    "",
    "is_allowlisted() {",
    '  local file="$1"',
    '  for entry in "${allowlist[@]}"; do',
    '    if [[ "$file" == "$entry" ]]; then',
    "      return 0",
    "    fi",
    "  done",
    "  return 1",
    "}",
    "",
    "# Collect the input file list: positional args override stdin.",
    "if [[ $# -gt 0 ]]; then",
    '  files=("$@")',
    "else",
    "  files=()",
    "  while IFS= read -r line; do",
    '    [[ -z "$line" ]] && continue',
    '    files+=("$line")',
    "  done",
    "fi",
    "",
    "if [[ ${#files[@]} -eq 0 ]]; then",
    "  exit 0",
    "fi",
    "",
    "bundle_regexes=()",
    'for pattern in "${bundle_patterns[@]}"; do',
    '  bundle_regexes+=("$(glob_to_regex "$pattern")")',
    "done",
    "",
    "violations=()",
    "",
    'for file in "${files[@]}"; do',
    '  if is_allowlisted "$file"; then',
    "    continue",
    "  fi",
    "  matches_pattern=0",
    '  for regex in "${bundle_regexes[@]}"; do',
    '    if [[ "$file" =~ $regex ]]; then',
    "      matches_pattern=1",
    "      break",
    "    fi",
    "  done",
    '  if [[ "$matches_pattern" -eq 0 ]]; then',
    "    continue",
    "  fi",
    '  if [[ ! -f "$file" ]]; then',
    "    continue",
    "  fi",
    '  if has_multiline_recipe "$file"; then',
    '    violations+=("$file")',
    "  fi",
    "done",
    "",
    "if [[ ${#violations[@]} -gt 0 ]]; then",
    '  err "inline \\`gh issue create\\` recipes found in the following files:"',
    '  for v in "${violations[@]}"; do',
    '    err "  - $v"',
    "  done",
    "  err \"move each recipe into a '## Template: <phase-label>' section of '$templates_path' and cite that section from the bundle rule\"",
    "  exit 1",
    "fi",
    "",
    "exit 0",
  ].join("\n");
}

/**
 * Render the `.claude/procedures/check-issue-template-labels.sh`
 * companion lint. Exported so `AgentConfig` can emit it alongside the
 * reference-don't-inline lint when the consumer opts in via
 * `emitChecker: true`.
 *
 * Where `check-issue-templates.sh` polices *where* recipes live, this
 * one polices *what they say*. For every
 * `## Template: <phase-label>` section on the templates page, its
 * router-style child pages, and the generated companion, it asserts:
 *
 * 1. The recipe passes `--label <phase-label>` — the heading and the
 *    command agree.
 * 2. It carries exactly one `type:*` label, and that label is the
 *    `type:<bundle>` the phase-label invariant requires.
 * 3. It carries a GitHub issue-type assignment step (the
 *    `set-issue-type.sh` helper or the `updateIssueIssueType` GraphQL
 *    flow it wraps) — an issue filed without one stays untyped forever.
 *
 * Sections whose heading matches no bundle-owned phase label are
 * skipped, not failed: unrecognised `foo:bar` labels are
 * consumer-specific and deliberately not policed, exactly as the
 * orchestrator's invariant sweep treats them.
 *
 * The phase-label → type-label resolver is rendered from the same
 * `PHASE_LABEL_TYPE_MAP` that drives the label registry and the
 * orchestrator sweep, so the lint can never enforce a stale pairing.
 */
export function renderIssueTemplateLabelsCheckerScript(
  it: ResolvedIssueTemplates,
): string {
  const generatedPath = issueTemplatesGeneratedPath(it.templatesPath);
  const childGlob = issueTemplatesChildGlob(it.templatesPath);

  return [
    "#!/usr/bin/env bash",
    "# check-issue-template-labels.sh — Enforce recipe label correctness.",
    "#",
    "# Usage:",
    "#   .claude/procedures/check-issue-template-labels.sh [<file>...]",
    "#",
    "# With no arguments the lint walks the templates page, its",
    "# router-style child pages, and the generated label-set companion.",
    "# Positional arguments override that default set.",
    "#",
    "# For every `## Template: <phase-label>` section it asserts the",
    "# recipe passes the heading's phase label, carries exactly one",
    "# `type:*` label matching the phase-label invariant, and assigns a",
    "# GitHub issue type. Sections whose heading matches no bundle-owned",
    "# phase label are skipped — unrecognised labels are consumer-",
    "# specific and deliberately not policed.",
    "#",
    "# The resolver below is generated from the canonical bundle-",
    "# ownership map — do not edit by hand; regenerate via",
    "# `pnpm exec projen`.",
    "",
    "set -uo pipefail",
    "",
    "err() {",
    '  printf "check-issue-template-labels.sh: %s\\n" "$*" >&2',
    "}",
    "",
    renderPhaseTypeInvariantShellHelpers(),
    "",
    `templates_path=${JSON.stringify(it.templatesPath)}`,
    `generated_path=${JSON.stringify(generatedPath)}`,
    `child_glob=${JSON.stringify(childGlob)}`,
    "",
    "# Emit one TAB-separated record per `## Template:` section:",
    "#   <file>\\t<line>\\t<phase-label>\\t<comma-joined labels>\\t<0|1 typed>",
    "# Quote characters are folded to spaces up front so a recipe written",
    "# with single quotes, double quotes, or none at all parses the same.",
    "# Fenced blocks are tracked so the `## Objective` heading inside a",
    "# recipe's --body string never reads as the end of the section.",
    "scan_file() {",
    '  local file="$1"',
    "  tr '\\042\\047' '  ' < \"$file\" | awk -v FNAME=\"$file\" '",
    "    function flush() {",
    '      if (phase != "") {',
    '        printf "%s\\t%d\\t%s\\t%s\\t%d\\n", FNAME, start, phase, labels, hastype;',
    "      }",
    '      phase = ""; labels = ""; hastype = 0;',
    "    }",
    "    {",
    "      if ($0 ~ /^[ \\t]*```/) {",
    "        fence = 1 - fence;",
    '      } else if (fence == 0 && index($0, "## ") == 1) {',
    "        flush();",
    '        if (index($0, "## Template: ") == 1) {',
    "          phase = substr($0, 14);",
    '          gsub(/[ \\t`]/, "", phase);',
    "          start = NR;",
    "        }",
    "        next;",
    "      }",
    '      if (phase == "") next;',
    "      if ($0 ~ /set-issue-type\\.sh/ || $0 ~ /updateIssueIssueType/) {",
    "        hastype = 1;",
    "      }",
    "      line = $0;",
    "      while (match(line, /--label[ \\t]+[^ \\t]+/)) {",
    "        tok = substr(line, RSTART, RLENGTH);",
    "        line = substr(line, RSTART + RLENGTH);",
    '        sub(/^--label[ \\t]+/, "", tok);',
    '        gsub(/[\\\\`]/, "", tok);',
    '        labels = labels tok ",";',
    "      }",
    "    }",
    "    END { flush(); }",
    "  '",
    "}",
    "",
    "# Collect the file list: positional args override the default set.",
    "files=()",
    "if [[ $# -gt 0 ]]; then",
    '  files=("$@")',
    "else",
    '  [[ -f "$templates_path" ]] && files+=("$templates_path")',
    '  [[ -f "$generated_path" ]] && files+=("$generated_path")',
    "  for child in $child_glob; do",
    '    [[ -f "$child" ]] && files+=("$child")',
    "  done",
    "fi",
    "",
    "if [[ ${#files[@]} -eq 0 ]]; then",
    "  exit 0",
    "fi",
    "",
    "violations=()",
    "checked=0",
    "",
    'for file in "${files[@]}"; do',
    '  [[ -f "$file" ]] || continue',
    "  while IFS=$'\\t' read -r rec_file rec_line phase labels hastype; do",
    '    [[ -z "$phase" ]] && continue',
    '    required=$(phase_label_type_of "$phase")',
    '    [[ -z "$required" ]] && continue',
    "    checked=$(( checked + 1 ))",
    '    case ",${labels}" in',
    '      *",${phase},"*) ;;',
    "      *) violations+=(\"$rec_file:$rec_line: recipe for '$phase' never passes --label $phase\") ;;",
    "    esac",
    '    type_labels=""',
    "    type_count=0",
    '    for entry in $(printf "%s" "$labels" | tr , " "); do',
    '      case "$entry" in',
    "        type:*)",
    '          type_labels="${type_labels}${entry} "',
    "          type_count=$(( type_count + 1 ))",
    "          ;;",
    "      esac",
    "    done",
    '    if [[ "$type_count" -eq 0 ]]; then',
    "      violations+=(\"$rec_file:$rec_line: recipe for '$phase' carries no type:* label (expected $required)\")",
    '    elif [[ "$type_count" -gt 1 ]]; then',
    "      violations+=(\"$rec_file:$rec_line: recipe for '$phase' carries ${type_count} type:* labels (${type_labels% }); exactly one is allowed\")",
    '    elif [[ "${type_labels% }" != "$required" ]]; then',
    "      violations+=(\"$rec_file:$rec_line: recipe for '$phase' carries ${type_labels% } but the phase label requires $required\")",
    "    fi",
    '    if [[ "$hastype" != "1" ]]; then',
    "      violations+=(\"$rec_file:$rec_line: recipe for '$phase' has no GitHub issue-type assignment step\")",
    "    fi",
    '  done < <(scan_file "$file")',
    "done",
    "",
    "if [[ ${#violations[@]} -gt 0 ]]; then",
    '  err "issue-template recipes disagree with the phase-label invariant:"',
    '  for v in "${violations[@]}"; do',
    '    err "  - $v"',
    "  done",
    "  err \"each '## Template: <phase-label>' recipe must pass its own phase label, exactly one matching type:<bundle> label, and a set-issue-type.sh (or updateIssueIssueType) step\"",
    "  exit 1",
    "fi",
    "",
    'printf "check-issue-template-labels.sh: %d recipe(s) OK\\n" "$checked"',
    "exit 0",
  ].join("\n");
}

/*******************************************************************************
 *
 * Internal helpers
 *
 ******************************************************************************/

/** Final path segment of a repo-relative path. */
function basename(path: string): string {
  const slash = path.lastIndexOf("/");
  return slash === -1 ? path : path.slice(slash + 1);
}

function assertValidTemplatesPath(value: string): void {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(
      `IssueTemplatesConfig.templatesPath must be a non-empty string; got ${JSON.stringify(
        value,
      )}`,
    );
  }
  if (value.startsWith("/")) {
    throw new Error(
      `IssueTemplatesConfig.templatesPath must be a repo-relative path (no leading '/'); got ${JSON.stringify(
        value,
      )}`,
    );
  }
}

function assertValidBundlePathPatterns(value: ReadonlyArray<string>): void {
  if (!Array.isArray(value)) {
    throw new Error(
      `IssueTemplatesConfig.bundlePathPatterns must be an array; got ${JSON.stringify(
        value,
      )}`,
    );
  }
  if (value.length === 0) {
    throw new Error(
      "IssueTemplatesConfig.bundlePathPatterns must not be empty — supply at least one pattern or omit the field to use the defaults",
    );
  }
  for (const entry of value) {
    if (typeof entry !== "string" || entry.trim().length === 0) {
      throw new Error(
        `IssueTemplatesConfig.bundlePathPatterns entries must be non-empty strings; got ${JSON.stringify(
          entry,
        )}`,
      );
    }
  }
}
